// // ─────────────────────────────────────────────────────────────────────────────
// // SHARED — IncomingCallModal.jsx
// // Used by both Doctor app and Patient app (identical logic).
// //
// // KEY BEHAVIOUR:
// //   - Filters out notifications for the room the user is ALREADY IN.
// //     This covers the "joined via appointment page" scenario:
// //     the other side may have a status-1 doc in Firestore, but since
// //     selectedAppt.appointmentId matches, we suppress it here.
// //   - Renders nothing if no visible calls remain after filtering.
// // ─────────────────────────────────────────────────────────────────────────────
// /* eslint-disable react-hooks/exhaustive-deps */
// import React from "react";
// import { useSelector } from "react-redux";
// import { FaPhone } from "react-icons/fa6";
// import { FaVideo } from "react-icons/fa6";

// /**
//  * @param {object[]} data        - Array of call notification objects from Firestore
//  * @param {function} onAccept    - (roomId: string) => void
//  * @param {function} onReject    - (roomId: string) => void
//  */
// export default function IncomingCallModal({ data, onAccept, onReject }) {
//   // Works for both apps:
//   //   Doctor app stores selected appointment in state.user.selectedAppt
//   //   Patient app stores selected appointment in state.appt.selectedAppt
//   // Pass the right selector from the parent (via a prop or context),
//   // or keep two versions of this file with the correct selector.
//   // Here we try both; whichever returns a value is used.
//   const patientSelectedAppt = useSelector((state) => state.appt?.selectedAppt);
//   const selectedAppt =  patientSelectedAppt;

//   if (!data || data.length === 0) return null;

//   /**
//    * KEY FIX — "Join via appointment page" scenario:
//    *
//    * If the user is already inside a video room (selectedAppt is set),
//    * we filter out any notification whose roomId matches that appointment.
//    * This prevents showing a call popup to someone who is already in the call.
//    *
//    * Why this works:
//    * - Person A rejects the call notification but navigates to the video room
//    *   via the appointment card/dashboard. At that point selectedAppt is set
//    *   to that appointmentId.
//    * - The Firestore doc may still have status == 1 (if Person B hasn't left yet),
//    *   so the snapshot listener still returns it.
//    * - But this filter hides it because the roomId matches selectedAppt.
//    */
//   const visibleCalls = data
//   // .filter((item) =>
//   //   selectedAppt
//   //     ? item.incomingCall.roomId !== selectedAppt.appointmentId
//   //     : true
//   // );

//   if (visibleCalls.length === 0) return null;

//   const getInitial = (name) => {
//     if (!name) return "?";
//     const cleaned = name.replace(/^(Dr|Mr|Mrs|Ms|Miss)\.?\s*/i, "").trim();
//     return cleaned[0]?.toUpperCase() || "?";
//   };

//   return (
//     <div className="incoming-call-container">
//       {visibleCalls.map((item) => (
//         <div
//           key={item.incomingCall.roomId}
//           className="relative w-[370px] rounded-2xl p-[2px] bg-gradient-to-r from-[#76ADCC] via-[#5fb3b0] to-[#6bc7a6] shadow-xl"
//         >
//           <div className="relative rounded-md bg-gradient-to-r from-[#6fb0cc]/95 via-[#5fb3b0]/95 to-[#6bc7a6]/95 backdrop-blur-md px-4 py-2 text-white">
//             <div className="flex items-center gap-3">
//               <div className="relative flex items-center justify-center w-10 h-10 rounded-full border-2 border-white/70 text-lg font-semibold">
//                 {getInitial(item?.incomingCall?.callerName)}
//                 <span className="absolute top-1 right-1 w-2 h-2 bg-green-400 rounded-full border-2 border-white" />
//               </div>
//               <div>
//                 <p className="text-lg opacity-90">Incoming Call</p>
//                 <h2 className="text-lg font-semibold mt-1">
//                   {item?.incomingCall?.callerName}
//                 </h2>
//                 <p className="opacity-80 mt-1">is waiting for you...</p>
//               </div>
//             </div>

//             <div className="h-px bg-white/30 my-2" />

//             <div className="flex items-center justify-between px-12">
//               <div className="flex flex-col items-center gap-3">
//                 <button
//                   onClick={() => onReject(item?.incomingCall?.roomId)}
//                   className="w-10 h-10 rounded-full bg-white text-teal-500 flex items-center justify-center shadow-lg hover:scale-105 active:scale-95 transition"
//                 >
//                   <FaPhone className="text-xl" />
//                 </button>
//                 <span className="text-base opacity-90">Decline</span>
//               </div>

//               <div className="flex flex-col items-center gap-3">
//                 <button
//                   onClick={() => onAccept(item?.incomingCall?.roomId)}
//                   className="w-10 h-10 rounded-full bg-gradient-to-br from-[#64d2b0] to-[#49a6a6] flex items-center justify-center shadow-lg hover:scale-105 active:scale-95 transition"
//                 >
//                   <FaVideo className="text-xl" />
//                 </button>
//                 <span className="text-base opacity-90">Join Call</span>
//               </div>
//             </div>
//           </div>
//         </div>
//       ))}
//     </div>
//   );
// }

// ─────────────────────────────────────────────────────────────────────────────
// SHARED — IncomingCallModal.jsx (Patient app copy)
// Identical logic to the Doctor app version — only the Redux selector differs.
// ─────────────────────────────────────────────────────────────────────────────
/* eslint-disable react-hooks/exhaustive-deps */
// import React, { useCallback, useEffect, useState } from "react";
// import { useSelector } from "react-redux";
// import { FaPhone, FaVideo, FaXmark } from "react-icons/fa6";

// const AUTO_DISMISS_MS = 30_000; // 30 seconds per-card
// const TICK_MS = 1_000; // how often we check for expired cards

// /**
//  * @param {object[]} data        - Array of call notification objects from Firestore
//  * @param {function} onAccept    - (roomId: string) => void
//  * @param {function} onReject    - (roomId: string) => void
//  */
// export default function IncomingCallModal({ data, onAccept, onReject }) {
//   // Patient app stores selected appointment in state.appt.selectedAppt
//   // eslint-disable-next-line no-unused-vars
//   const selectedAppt = useSelector((state) => state.appt?.selectedAppt);

//   // Local list of cards. Each item remembers its OWN `receivedAt`, so a new
//   // notification arriving never resets or affects any other card's timer.
//   const [items, setItems] = useState([]);

//   // ─── Sync incoming Firestore `data` into local `items` ─────────────────
//   // Pure state derivation only — no timers/side-effects scheduled here.
//   // - New roomId  -> added with receivedAt = now.
//   // - Existing roomId -> kept exactly as-is (receivedAt untouched), payload refreshed.
//   // - roomId no longer in `data` (handled/expired elsewhere) -> dropped.
//   useEffect(() => {
//     if (!data) return;

//     setItems((prevItems) => {
//       const prevMap = new Map(prevItems.map((it) => [it.roomId, it]));
//       const nextItems = [];

//       data.forEach((callDoc) => {
//         const roomId = callDoc?.incomingCall?.roomId;
//         if (!roomId) return;

//         const existing = prevMap.get(roomId);
//         nextItems.push(
//           existing
//             ? { ...existing, callDoc } // preserve receivedAt
//             : { roomId, callDoc, receivedAt: Date.now() } // new card, new timer start
//         );
//       });

//       return nextItems;
//     });
//   }, [data]);

//   // ─── Independent 30s expiry, driven by a single interval ───────────────
//   // Each card is removed exactly 30s after ITS OWN receivedAt. Using one
//   // interval instead of N setTimeouts avoids scheduling side-effects inside
//   // setState updaters (which can double-fire under React Strict Mode /
//   // concurrent rendering) — this approach is idempotent by construction.
//   useEffect(() => {
//     const interval = setInterval(() => {
//       setItems((prev) => {
//         const now = Date.now();
//         const filtered = prev.filter((it) => now - it.receivedAt < AUTO_DISMISS_MS);
//         return filtered.length === prev.length ? prev : filtered;
//       });
//     }, TICK_MS);

//     return () => clearInterval(interval);
//   }, []);

//   const removeItem = useCallback((roomId) => {
//     setItems((prev) => prev.filter((it) => it.roomId !== roomId));
//   }, []);

//   const handleClose = useCallback(
//     (roomId) => {
//       onReject(roomId); // existing decline handler, for this card only
//       removeItem(roomId); // remove only this card, immediately
//     },
//     [onReject, removeItem]
//   );

//   // (kept as-is / disabled, matching current behavior)
//   const visibleItems = items;
//   // .filter((it) =>
//   //   selectedAppt ? it.roomId !== selectedAppt.appointmentId : true
//   // );

//   if (!visibleItems || visibleItems.length === 0) return null;

//   const getInitial = (name) => {
//     if (!name) return "?";
//     const cleaned = name.replace(/^(Dr|Mr|Mrs|Ms|Miss)\.?\s*/i, "").trim();
//     return cleaned[0]?.toUpperCase() || "?";
//   };

//   return (
//     <div className="incoming-call-container fixed top-4 right-4 z-[9999] flex flex-col gap-3 items-end">
//       {visibleItems.map(({ roomId, callDoc }) => {
//         const incomingCall = callDoc?.incomingCall || {};
//         return (
//           <div
//             key={roomId}
//             className="group relative w-[370px] rounded-2xl p-[2px] bg-gradient-to-r from-[#76ADCC] via-[#5fb3b0] to-[#6bc7a6] shadow-xl"
//           >
//             {/* Close (x) icon:
//                 - hidden by default on hover-capable devices, shown on hover/focus
//                 - always visible (dimmed) on touch devices, since there is no hover there */}
//             <button
//               type="button"
//               onClick={() => handleClose(roomId)}
//               aria-label="Dismiss notification"
//               className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white text-gray-600 shadow-md flex items-center justify-center z-10 hover:bg-gray-100 transition-opacity opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-70"
//             >
//               <FaXmark className="text-xs" />
//             </button>

//             <div className="relative rounded-md bg-gradient-to-r from-[#6fb0cc]/95 via-[#5fb3b0]/95 to-[#6bc7a6]/95 backdrop-blur-md px-4 py-2 text-white">
//               <div className="flex items-center gap-3">
//                 <div className="relative flex items-center justify-center w-10 h-10 rounded-full border-2 border-white/70 text-lg font-semibold">
//                   {getInitial(incomingCall?.callerName)}
//                   <span className="absolute top-1 right-1 w-2 h-2 bg-green-400 rounded-full border-2 border-white" />
//                 </div>
//                 <div>
//                   <p className="text-lg opacity-90">Incoming Call Notification</p>
//                   <h2 className="text-lg font-semibold mt-1">
//                     {incomingCall?.callerName}
//                   </h2>
//                   <p className="opacity-80 mt-1">is waiting for you...</p>
//                 </div>
//               </div>

//               <div className="h-px bg-white/30 my-2" />

//               <div className="flex items-center justify-between px-12">
//                 <div className="flex flex-col items-center gap-3">
//                   <button
//                     onClick={() => onReject(incomingCall?.roomId)}
//                     className="w-10 h-10 rounded-full bg-white text-teal-500 flex items-center justify-center shadow-lg hover:scale-105 active:scale-95 transition"
//                   >
//                     <FaPhone className="text-xl" />
//                   </button>
//                   <span className="text-base opacity-90">Decline</span>
//                 </div>

//                 <div className="flex flex-col items-center gap-3">
//                   <button
//                     onClick={() => onAccept(incomingCall?.roomId)}
//                     className="w-10 h-10 rounded-full bg-gradient-to-br from-[#64d2b0] to-[#49a6a6] flex items-center justify-center shadow-lg hover:scale-105 active:scale-95 transition"
//                   >
//                     <FaVideo className="text-xl" />
//                   </button>
//                   <span className="text-base opacity-90">Join Call</span>
//                 </div>
//               </div>
//             </div>
//           </div>
//         );
//       })}
//     </div>
//   );
// }


/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { FaPhone, FaVideo, FaXmark } from "react-icons/fa6";

const AUTO_DISMISS_MS = 30_000; // 30 seconds per-card
const TICK_MS = 1_000; // how often we check for expired cards

/**
 * @param {object[]} data        - Array of call notification objects from Firestore
 * @param {function} onAccept    - (roomId: string) => void
 * @param {function} onReject    - (roomId: string) => void
 */
export default function IncomingCallModal({ data, onAccept, onReject }) {
  // Patient app stores selected appointment in state.appt.selectedAppt
  // eslint-disable-next-line no-unused-vars
  const selectedAppt = useSelector((state) => state.appt?.selectedAppt);

  // Local list of cards. Each item remembers its OWN `receivedAt`, so a new
  // notification arriving never resets or affects any other card's timer.
  const [items, setItems] = useState([]);

  // Mirror of `items` for reading inside the interval callback without
  // creating a stale closure and without re-creating the interval on every
  // items change.
  const itemsRef = useRef(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // ─── Sync incoming Firestore `data` into local `items` ─────────────────
  // Pure state derivation only.
  // - New roomId  -> added with receivedAt = now.
  // - Existing roomId -> kept exactly as-is (receivedAt untouched), payload refreshed.
  // - roomId no longer in `data` (handled/expired elsewhere) -> dropped.
  useEffect(() => {
    if (!data) return;

    setItems((prevItems) => {
      const prevMap = new Map(prevItems.map((it) => [it.roomId, it]));
      const nextItems = [];

      data.forEach((callDoc) => {
        const roomId = callDoc?.incomingCall?.roomId;
        if (!roomId) return;

        const existing = prevMap.get(roomId);
        nextItems.push(
          existing
            ? { ...existing, callDoc } // preserve receivedAt
            : { roomId, callDoc, receivedAt: Date.now() } // new card, new timer start
        );
      });

      return nextItems;
    });
  }, [data]);

  // ─── Independent 30s expiry, driven by a single interval ───────────────
  // Each card is checked against ITS OWN receivedAt. When a card expires we:
  //   1. Call onReject(roomId) — same decline handler as the close/decline
  //      button — so Firestore actually reflects the call as declined.
  //      Without this, a page reload re-fetches the still-"ringing" doc
  //      from Firestore and the card reappears.
  //   2. Remove the card locally.
  // Side effects (onReject) are called from the interval callback itself,
  // NOT from inside a setState updater, so this is safe under Strict Mode.
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const current = itemsRef.current;
      const expired = current.filter(
        (it) => now - it.receivedAt >= AUTO_DISMISS_MS
      );
      if (expired.length === 0) return;

      expired.forEach((it) => {
        onReject(it.roomId);
      });

      setItems((prev) =>
        prev.filter((it) => now - it.receivedAt < AUTO_DISMISS_MS)
      );
    }, TICK_MS);

    return () => clearInterval(interval);
  }, [onReject]);

  const removeItem = useCallback((roomId) => {
    setItems((prev) => prev.filter((it) => it.roomId !== roomId));
  }, []);

  const handleClose = useCallback(
    (roomId) => {
      onReject(roomId); // existing decline handler, for this card only
      removeItem(roomId); // remove only this card, immediately
    },
    [onReject, removeItem]
  );

  // (kept as-is / disabled, matching current behavior)
  const visibleItems = items;
  // .filter((it) =>
  //   selectedAppt ? it.roomId !== selectedAppt.appointmentId : true
  // );

  if (!visibleItems || visibleItems.length === 0) return null;

  const getInitial = (name) => {
    if (!name) return "?";
    const cleaned = name.replace(/^(Dr|Mr|Mrs|Ms|Miss)\.?\s*/i, "").trim();
    return cleaned[0]?.toUpperCase() || "?";
  };

  return (
    <div className="incoming-call-container fixed top-4 right-4 z-[9999] flex flex-col gap-3 items-end">
      {visibleItems.map(({ roomId, callDoc }) => {
        const incomingCall = callDoc?.incomingCall || {};
        return (
          <div
            key={roomId}
            className="group relative w-[370px] rounded-2xl p-[2px] bg-gradient-to-r from-[#76ADCC] via-[#5fb3b0] to-[#6bc7a6] shadow-xl"
          >
            {/* Close (x) icon:
                - hidden by default on hover-capable devices, shown on hover/focus
                - always visible (dimmed) on touch devices, since there is no hover there */}
            <button
              onClick={() => handleClose(roomId)}
              aria-label="Dismiss notification"
              className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white text-gray-600 shadow-md flex items-center justify-center z-10 hover:bg-gray-100 transition-opacity opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-70"
            >
              <FaXmark className="text-xs" />
            </button>

            <div className="relative rounded-md bg-gradient-to-r from-[#6fb0cc]/95 via-[#5fb3b0]/95 to-[#6bc7a6]/95 backdrop-blur-md px-4 py-2 text-white">
              <div className="flex items-center gap-3">
                <div className="relative flex items-center justify-center w-10 h-10 rounded-full border-2 border-white/70 text-lg font-semibold">
                  {getInitial(incomingCall?.callerName)}
                  <span className="absolute top-1 right-1 w-2 h-2 bg-green-400 rounded-full border-2 border-white" />
                </div>
                <div>
                  <p className="text-lg opacity-90">Incoming Call Notification</p>
                  <h2 className="text-lg font-semibold mt-1">
                    {incomingCall?.callerName}
                  </h2>
                  <p className="opacity-80 mt-1">is waiting for you...</p>
                </div>
              </div>

              <div className="h-px bg-white/30 my-2" />

              <div className="flex items-center justify-between px-12">
                <div className="flex flex-col items-center gap-3">
                  <button
                    onClick={() => onReject(incomingCall?.roomId)}
                    className="w-10 h-10 rounded-full bg-white text-teal-500 flex items-center justify-center shadow-lg hover:scale-105 active:scale-95 transition"
                  >
                    <FaPhone className="text-xl" />
                  </button>
                  <span className="text-base opacity-90">Decline</span>
                </div>

                <div className="flex flex-col items-center gap-3">
                  <button
                    onClick={() => onAccept(incomingCall?.roomId)}
                    className="w-10 h-10 rounded-full bg-gradient-to-br from-[#64d2b0] to-[#49a6a6] flex items-center justify-center shadow-lg hover:scale-105 active:scale-95 transition"
                  >
                    <FaVideo className="text-xl" />
                  </button>
                  <span className="text-base opacity-90">Join Call</span>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}